import { ITabsVisibility } from '@models/search/ITabsVisibility';
import { IOrderByItem, SearchMode, SearchScope, SEARCH_SCOPES, isSearchScope } from '@models/search/search.types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from '@/store/reducers/SearchReducer';

/**
 * Контракт URL страницы поиска — единственный источник истины для состояния, которое
 * должно переживать refresh и передаваться ссылкой:
 *
 *     /search?text=Закупка&tab=dicts&page=2&size=20&sort=name.asc&mode=ai
 *
 * `page/size/sort` относятся к активной области: при смене вкладки они сбрасываются,
 * поэтому адресная строка всегда описывает ровно то, что видно на экране.
 */
export const SEARCH_PARAM = {
    text: 'text',
    tab: 'tab',
    page: 'page',
    size: 'size',
    sort: 'sort',
    mode: 'mode',
} as const;

export interface ISortOption {
    propName: string;
    label: string;
}

export const SCOPE_SORT_OPTIONS: Record<SearchScope, ISortOption[]> = {
    docs: [
        { propName: 'creationDate', label: 'Дата регистрации' },
        { propName: 'price', label: 'Цена' },
    ],
    dicts: [{ propName: 'name', label: 'Наименование' }],
    reports: [{ propName: 'name', label: 'Наименование' }],
};

const SCOPE_HIDDEN_KEY: Record<SearchScope, keyof ITabsVisibility> = {
    docs: 'docsTabHidden',
    dicts: 'dictsTabHidden',
    reports: 'reportsTabHidden',
};

export interface ISearchRouteState {
    text: string;
    scope: SearchScope;
    mode: SearchMode;
    /** Нумерация с нуля; в URL страница человеческая, с единицы. */
    pageIndex: number;
    pageSize: number;
    orderBy?: IOrderByItem;
}

/** Области, разрешённые конфигурацией. `undefined` — конфигурация ещё не пришла. */
export const getAvailableScopes = (visibility: ITabsVisibility | undefined): SearchScope[] | undefined =>
    visibility ? SEARCH_SCOPES.filter((scope) => !visibility[SCOPE_HIDDEN_KEY[scope]]) : undefined;

export const formatOrderBy = (orderBy: IOrderByItem | undefined): string =>
    orderBy ? `${orderBy.propName}.${orderBy.asc ? 'asc' : 'desc'}` : '';

export const parseOrderBy = (raw: string | null, scope: SearchScope): IOrderByItem | undefined => {
    if (!raw) {
        return undefined;
    }
    const separator = raw.lastIndexOf('.');
    const propName = separator === -1 ? raw : raw.slice(0, separator);
    const direction = separator === -1 ? 'asc' : raw.slice(separator + 1);

    if (!SCOPE_SORT_OPTIONS[scope].some((option) => option.propName === propName)) {
        return undefined;
    }
    return { propName, asc: direction !== 'desc' };
};

const parsePositiveInt = (raw: string | null, fallback: number): number => {
    const value = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : fallback;
};

export interface IParsedSearchRoute extends ISearchRouteState {
    /** URL содержал значения, которые пришлось привести к допустимым — их нужно переписать. */
    normalized: boolean;
}

/**
 * Разбор и нормализация URL. Неподдерживаемая или скрытая конфигурацией область
 * заменяется первой доступной; `mode=ai` вне документов снимается — AI-поиск
 * реализован только для них.
 */
export const parseSearchRoute = (
    params: URLSearchParams,
    availableScopes: SearchScope[] | undefined,
): IParsedSearchRoute => {
    const rawTab = params.get(SEARCH_PARAM.tab);
    const requestedScope: SearchScope = isSearchScope(rawTab) ? rawTab : 'docs';

    let scope = requestedScope;
    if (availableScopes && availableScopes.length > 0 && !availableScopes.includes(scope)) {
        scope = availableScopes[0];
    }

    const rawMode = params.get(SEARCH_PARAM.mode);
    const mode: SearchMode = rawMode === 'ai' && scope === 'docs' ? 'ai' : 'exact';

    const rawSize = parsePositiveInt(params.get(SEARCH_PARAM.size), DEFAULT_PAGE_SIZE);
    const pageSize = PAGE_SIZES.includes(rawSize) ? rawSize : DEFAULT_PAGE_SIZE;
    const pageIndex = parsePositiveInt(params.get(SEARCH_PARAM.page), 1) - 1;
    const orderBy = parseOrderBy(params.get(SEARCH_PARAM.sort), scope);

    const state: ISearchRouteState = {
        text: (params.get(SEARCH_PARAM.text) ?? '').trim(),
        scope,
        mode,
        pageIndex,
        pageSize,
        orderBy,
    };

    const canonical = buildSearchParams(state);
    const normalized = Object.values(SEARCH_PARAM).some(
        (key) => (params.get(key) ?? '') !== (canonical.get(key) ?? ''),
    );

    return { ...state, normalized };
};

/**
 * Запись состояния в адресную строку. Значения по умолчанию не пишутся, чужие
 * параметры (`returnTo` и т. п.) сохраняются — переписываются только ключи поиска.
 */
export const applySearchState = (params: URLSearchParams, state: ISearchRouteState): URLSearchParams => {
    const next = new URLSearchParams(params);
    const write = (key: string, value: string) => (value ? next.set(key, value) : next.delete(key));

    write(SEARCH_PARAM.text, state.text);
    write(SEARCH_PARAM.tab, state.scope === 'docs' ? '' : state.scope);
    write(SEARCH_PARAM.mode, state.mode === 'ai' ? 'ai' : '');
    write(SEARCH_PARAM.page, state.pageIndex > 0 ? String(state.pageIndex + 1) : '');
    write(SEARCH_PARAM.size, state.pageSize === DEFAULT_PAGE_SIZE ? '' : String(state.pageSize));
    write(SEARCH_PARAM.sort, formatOrderBy(state.orderBy));

    return next;
};

/** Сборка адресной строки «с нуля» — например, при переходе из панели поиска. */
export const buildSearchParams = (state: ISearchRouteState): URLSearchParams =>
    applySearchState(new URLSearchParams(), state);
